/**
 * lib/sla.js — pure helpers behind the case-file response-SLA chip
 * (sla_escalation_logic.md §7). No React, no fetch — unit-tested under node.
 *
 * Input is GET /api/cases/{id}/sla:
 *   { enabled, hoursPerLeg, serverNow, open: leg | null, remainingMs, legs: [...] }
 *   leg = { legNo, ownerSide: 'hq'|'clinic', dueAt, pausedAt, pausedReason,
 *           pausedByName, breachedAt, breachCount, closedAt, closedReason,
 *           snoozeDays, snoozeCommentId, snoozeUntil, ... }          (mig 028)
 *
 * Snooze (sla_escalation_logic.md §4.1): a Supervisor's public message may carry
 * snoozeDays; the clinic leg it opens records it PENDING (snoozeUntil null).
 * Once a clinic user acknowledges that message, snoozeUntil is set and the due
 * date moves to snoozeUntil + hoursPerLeg working hours — the chip reads
 * "Snoozed until <date>" while snoozeUntil is ahead.
 *
 * Chip wording rule: the chip is a glance, not a log. A moment 12h+ away reads
 * as a date ("until <date>", "due <date>"); inside 12h it reads as a countdown
 * ("3h 20m left"). Who did what, and when, lives in History and the tooltip.
 */

export const SLA_SIDE_LABELS = Object.freeze({ hq: 'Supervisor', clinic: 'Clinic' });

// ── Snooze (composer control + badges) ───────────────────────────────────────

export const SNOOZE_DAYS_MIN = 1;
export const SNOOZE_DAYS_MAX = 30;
/** The quick picks in the composer; anything else 1–30 goes through "Custom". */
export const SNOOZE_DAY_PRESETS = Object.freeze([1, 2, 3, 4, 5, 7, 10, 14]);

/** Snooze days as the API accepts them: an integer 1..30, else null (mirrors the server). */
export function normaliseSnoozeDays(v) {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  if (!Number.isInteger(n) || n < SNOOZE_DAYS_MIN || n > SNOOZE_DAYS_MAX) return null;
  return n;
}

/** "4 days" / "1 day". */
export function snoozeDaysLabel(days) {
  const n = Number(days) || 0;
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** The helper line under the composer control. */
export function snoozeHelperText(hoursPerLeg = 6) {
  return `Once the clinic acknowledges this message, their ${hoursPerLeg}-hour response window starts after the snooze.`;
}

/** An open leg is "snoozed" while its snoozeUntil lies ahead and it is not on hold. */
export function isSnoozed(leg, now = Date.now()) {
  if (!leg || !leg.snoozeUntil || leg.pausedAt) return false;
  const t = Date.parse(leg.snoozeUntil);
  return Number.isFinite(t) && t > now;
}

/**
 * The badge a posted comment shows for its snooze, or null when it has none:
 *   pending — { state: 'pending', text: 'Snooze · N days', title: <sentence> }
 *   applied — { state: 'snoozed', text: 'Snoozed until <date>', title: <sentence>, until }
 * `text` is the pill (short — a pill is not a sentence); `title` is the hover text.
 * ONLY the snooze the OPEN leg carries is live. A snooze on an earlier leg —
 * superseded by a newer message, answered, or simply run out — is History; a
 * "Snoozed until" badge on it read as if it still counted.
 */
export function commentSnoozeBadge(comment, sla, { now = Date.now(), formatDateTime = (iso) => iso, formatDate = null } = {}) {
  const days = normaliseSnoozeDays(comment?.snoozeDays);
  if (!days) return null;
  const open = sla?.open;
  if (!open || Number(open.snoozeCommentId) !== Number(comment.id)) return null;
  const shortDate = formatDate || formatDateTime;
  if (open.snoozeUntil) {
    const t = Date.parse(open.snoozeUntil);
    if (!Number.isFinite(t) || t <= now) return null;   // run out — the clinic's clock is running
    return {
      state: 'snoozed', days, until: open.snoozeUntil,
      text:  `Snoozed until ${shortDate(open.snoozeUntil)}`,
      title: `Acknowledged by the clinic — their response window starts after ${formatDateTime(open.snoozeUntil)}.`,
    };
  }
  return {
    state: 'pending', days, until: null,
    text:  `Snooze · ${snoozeDaysLabel(days)}`,
    title: `Snoozes the response clock for ${snoozeDaysLabel(days)} once a clinic user acknowledges this message.`,
  };
}

/** Moments this far ahead read as a date on the chip; nearer ones as a countdown. */
export const CHIP_DATE_FROM_MS = 12 * 3_600_000;

/** true when `iso` is CHIP_DATE_FROM_MS or more ahead of `now`. */
export function isFarOff(iso, now = Date.now()) {
  const t = Date.parse(iso);
  return Number.isFinite(t) && t - now >= CHIP_DATE_FROM_MS;
}

/** Chip exists for every Treatment case with a severity code (Code 1–5) while the feature is on. */
export function slaApplies(caseLike, config) {
  if (!config || config.slaEnabled !== true) return false;
  if (!caseLike) return false;
  const type = caseLike.eventType || 'Treatment';
  return type === 'Treatment' && /^code\s*[1-5]$/i.test(String(caseLike.severity || '').trim());
}

/** ms → "2h 10m" | "45m" | "<1m"; negative → "0m". */
export function formatRemaining(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '0m';
  if (n < 60_000) return '<1m';
  const totalMin = Math.floor(n / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h >= 48) return `${Math.floor(h / 24)}d ${h % 24}h`;
  if (h > 0) return m ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
}

/** "3h ago" | "2d ago" | "just now" for a past ISO instant. */
export function formatAgo(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const diff = Math.max(0, now - t);
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * The live working-time remainder: the server's remainingMs (working ms at
 * serverNow) minus the wall-clock elapsed since. Close enough between refreshes;
 * a paused leg keeps its frozen remainder. Clamped at 0.
 */
export function liveRemainingMs(sla, now = Date.now()) {
  if (!sla?.open) return null;
  if (sla.remainingMs == null) return null;
  const base = Number(sla.remainingMs);
  if (!Number.isFinite(base)) return null;
  if (sla.open.pausedAt) return Math.max(0, base);
  const fetchedAt = Date.parse(sla.serverNow);
  const elapsed = Number.isFinite(fetchedAt) ? Math.max(0, now - fetchedAt) : 0;
  return Math.max(0, base - elapsed);
}

/**
 * buildSlaChip(sla, { now, formatDateTime }) → null | {
 *   state: 'awaiting' | 'hold_ack' | 'hold_client' | 'suppressed' | 'snoozed' | 'breached',
 *   tone:  'info' | 'warn' | 'danger' | 'muted',
 *   text:  the chip line,
 *   ownerSide, ownerLabel, dueAt, snoozeUntil?,
 * }
 * Holds win over a snooze (a held leg is not counting down); a live snooze wins
 * over an earlier breach (the clock was pushed past it on purpose).
 */
export function buildSlaChip(sla, { now = Date.now(), formatDateTime = (iso) => iso } = {}) {
  const leg = sla?.open;
  if (!leg) return null;
  const ownerLabel = SLA_SIDE_LABELS[leg.ownerSide] || leg.ownerSide || 'Owner';
  const due = leg.dueAt ? formatDateTime(leg.dueAt) : '—';
  const base = { ownerSide: leg.ownerSide, ownerLabel, dueAt: leg.dueAt };
  const farOff = isFarOff(leg.dueAt, now);
  const left = () => formatRemaining(liveRemainingMs(sla, now));

  if (leg.pausedReason === 'suppressed') {
    return { ...base, state: 'suppressed', tone: 'muted', text: `Response SLA suppressed${leg.pausedByName ? ` by ${leg.pausedByName}` : ''}` };
  }
  if (leg.pausedReason === 'waiting_on_client') {
    return { ...base, state: 'hold_client', tone: 'muted', text: 'On hold — Waiting on Client' };
  }
  // An acknowledgement holds the clock AND the reminder emails: the owner side
  // has said "seen", and it is now the Supervisor's call — lift the hold, snooze
  // it, or ask for more (which restarts the clinic's window). No date: a hold
  // has no end of its own, it lasts until someone acts.
  if (leg.pausedReason === 'acknowledged' || leg.pausedAt) {
    return { ...base, state: 'hold_ack', tone: 'warn', text: 'Waiting on Supervisor' };
  }
  if (isSnoozed(leg, now)) {
    const text = isFarOff(leg.snoozeUntil, now)
      ? `Snoozed until ${formatDateTime(leg.snoozeUntil)}`
      : `Snoozed — ends in ${formatRemaining(Date.parse(leg.snoozeUntil) - now)}`;
    return { ...base, state: 'snoozed', tone: 'info', snoozeUntil: leg.snoozeUntil, text };
  }
  if ((Number(leg.breachCount) || 0) > 0 && leg.breachedAt) {
    // The deadline is pinned; the chip counts WORKING hours overdue (server-computed —
    // the SPA has no calendar). The reminder count lives in History, not the chip.
    const over = Number.isFinite(Number(sla?.overdueWorkingMs))
      ? formatRemaining(Number(sla.overdueWorkingMs))
      : formatAgo(leg.breachedAt, now);
    return { ...base, state: 'breached', tone: 'danger', text: `${ownerLabel} response overdue by ${over}` };
  }
  const text = leg.ownerSide === 'clinic'
    ? (farOff ? `Clinic response due ${due}` : `Clinic response · ${left()} left`)
    : (farOff ? `Awaiting ${ownerLabel} response · due ${due}` : `Awaiting ${ownerLabel} response · ${left()} left`);
  return { ...base, state: 'awaiting', tone: 'info', text };
}

/** One line per leg for the chip tooltip (newest first, as the API returns). */
export function buildSlaHistoryLines(sla, { formatDateTime = (iso) => iso } = {}) {
  const legs = Array.isArray(sla?.legs) ? sla.legs : [];
  return legs.map(l => {
    const owner = SLA_SIDE_LABELS[l.ownerSide] || l.ownerSide;
    const outcome = l.closedAt
      ? `${(l.closedReason || 'closed').replace(/_/g, ' ')} ${formatDateTime(l.closedAt)}`
      : l.pausedReason ? `on hold (${l.pausedReason.replace(/_/g, ' ')})` : `due ${formatDateTime(l.dueAt)}`;
    const breaches = (Number(l.breachCount) || 0) > 0 ? ` · breached ×${l.breachCount}` : '';
    // Snooze: applied ("snoozed until …") or still waiting for the acknowledgement.
    const snooze = l.snoozeUntil ? ` · snoozed until ${formatDateTime(l.snoozeUntil)}`
      : normaliseSnoozeDays(l.snoozeDays) && !l.closedAt ? ` · snooze of ${snoozeDaysLabel(l.snoozeDays)} pending acknowledgement`
      : '';
    return `Leg ${l.legNo} · ${owner} · started ${formatDateTime(l.startedAt)} · ${outcome}${breaches}${snooze}`;
  });
}

/**
 * Which Supervisor actions apply to the open leg:
 *   lift     — an acknowledgement hold
 *   suppress — anything not already suppressed
 *   resume   — a suppressed leg
 */
export function slaActionsFor(leg) {
  if (!leg) return [];
  const out = [];
  if (leg.pausedReason === 'acknowledged') out.push('lift');
  if (leg.pausedReason === 'suppressed') out.push('resume');
  else out.push('suppress');
  return out;
}

/** "Acknowledged · 3" pill text + the hover list for a comment's acks. */
export function ackSummary(acks, { formatDateTime = (iso) => iso } = {}) {
  const list = Array.isArray(acks) ? acks : [];
  const mine = list.some(a => a?.mine);
  const label = list.length ? `Acknowledged · ${list.length}` : 'Acknowledge';
  const title = list.map(a => `${a.userName || 'Someone'} · ${a.createdAt ? formatDateTime(a.createdAt) : ''}`.trim()).join('\n');
  return { count: list.length, mine, label, title };
}

// ── All Cases table: SLA column ───────────────────────────────────────────────
// The list rows carry only the open leg's raw fields (see getCases list=1), not
// the working-hours remainder the case file computes. The column therefore
// speaks in DEADLINES (wall-clock to due_at — which was itself set in working
// hours) rather than "working time left": honest, cheap, and sortable.

/** Sort-key tiers: live countdowns/overdue first, then snoozed, then holds, then none. */
const SLA_TIER_SNOOZED = 1e14;
const SLA_TIER_HELD    = 2e14;
const SLA_TIER_NONE    = Number.POSITIVE_INFINITY;

/**
 * buildSlaCell(caseRow, { now, formatDate }) → null | { state, tone, text, title }
 *   state: 'overdue' | 'due' | 'snoozed' | 'hold_ack' | 'hold_client' | 'suppressed'
 *   tone:  'danger' | 'warn' | 'info' | 'muted'
 * Holds win over a snooze (a held leg is not counting down); a live snooze wins
 * over an earlier breach — same precedence as buildSlaChip.
 */
export function buildSlaCell(caseRow, { now = Date.now(), formatDate = (iso) => iso } = {}) {
  const leg = caseRow?.sla;
  if (!leg || !leg.ownerSide) return null;
  const owner = SLA_SIDE_LABELS[leg.ownerSide] || leg.ownerSide;
  if (leg.pausedReason === 'suppressed') {
    return { state: 'suppressed', tone: 'muted', text: 'Suppressed', title: 'Response SLA suppressed by a Supervisor' };
  }
  if (leg.pausedReason === 'waiting_on_client') {
    return { state: 'hold_client', tone: 'muted', text: 'On hold', title: 'On hold — Waiting on Client' };
  }
  if (leg.pausedReason === 'acknowledged' || leg.pausedAt) {
    return { state: 'hold_ack', tone: 'warn', text: 'Waiting on Supervisor', title: `Acknowledged — the ${owner} turn is paused until a Supervisor acts` };
  }
  const snoozeT = Date.parse(leg.snoozeUntil);
  if (Number.isFinite(snoozeT) && snoozeT > now) {
    return { state: 'snoozed', tone: 'info', text: `Snoozed · ${formatDate(leg.snoozeUntil)}`, title: `Snoozed — ${owner} turn resumes ${formatDate(leg.snoozeUntil)}` };
  }
  const dueT = Date.parse(leg.dueAt);
  if (!Number.isFinite(dueT)) {
    return { state: 'due', tone: 'info', text: `${owner} turn`, title: `${owner} response awaited` };
  }
  if (dueT <= now) {
    const over = compactSpan(now - dueT);
    const times = (Number(leg.breachCount) || 0) > 1 ? ` (×${leg.breachCount})` : '';
    return { state: 'overdue', tone: 'danger', text: `${owner} · ${over} over`, title: `${owner} response was due ${formatDate(leg.dueAt)}${times}` };
  }
  const left = dueT - now;
  const text = left >= 7 * 86_400_000
    ? `${owner} · due ${formatDate(leg.dueAt)}`
    : `${owner} · ${compactSpan(left)}`;
  return { state: 'due', tone: 'info', text, title: `${owner} response due ${formatDate(leg.dueAt)}` };
}

/** ms → "42m" | "3h 20m" | "1d 2h" | "3d" — two units at most, for a table cell. */
export function compactSpan(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '0m';
  const totalMin = Math.floor(n / 60_000);
  const h = Math.floor(totalMin / 60), m = totalMin % 60;
  if (h < 1)  return `${Math.max(1, m)}m`;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24), rh = h % 24;
  return rh ? `${d}d ${rh}h` : `${d}d`;
}

/**
 * slaSortKey(caseRow, now) → number; ascending = most urgent first.
 * Overdue (negative ms) < counting down (ms to due) < snoozed < held/suppressed < no SLA.
 */
export function slaSortKey(caseRow, now = Date.now()) {
  const leg = caseRow?.sla;
  if (!leg || !leg.ownerSide) return SLA_TIER_NONE;
  if (leg.pausedReason || leg.pausedAt) return SLA_TIER_HELD;
  const snoozeT = Date.parse(leg.snoozeUntil);
  if (Number.isFinite(snoozeT) && snoozeT > now) return SLA_TIER_SNOOZED + (snoozeT - now);
  const dueT = Date.parse(leg.dueAt);
  if (!Number.isFinite(dueT)) return SLA_TIER_SNOOZED - 1;
  return dueT - now;
}

/** Whole days since `iso` (floored, never negative); null when unknown. */
export function daysSince(iso, now = Date.now()) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 86_400_000));
}
